import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Home, Save, Code, Plus, Layers, Diamond } from 'lucide-react';
import CodeEditor from '@uiw/react-textarea-code-editor';
import { ExperienceJSON, ExperienceElement, Canvas, Animation, Keyframe, EasingFunction, LayoutTrigger, LayoutTriggerEvent, ElementStyle, ButtonAction, ElementState, ElementKeyframe, FontFamily, createEmptyExperience } from './types/experience';
import { createVariableState } from './utils/stateUtils';
import { parseDurationToSeconds, migrateToStates, calculateSnap, type SnapGuides as SnapGuidesType } from './components/ExperienceBuilder/utils';
import { findKeyframeIndexAtTime } from './utils/keyframeUtils';
import { PropertyInput, ColorPicker, Sidebar, TimelinePanel } from './components/ExperienceBuilder/Panels';
import { CanvasElement, SnapGuides } from './components/ExperienceBuilder/Canvas';
import { CustomSelect } from './components/ExperienceBuilder/CustomSelect';
import { AI_DOCS } from './experiences/aiDocs';
import './ExperienceBuilder.css';

interface Track {
  id: number;
  title: string;
  duration: string;
  audioUrl: string;
}

interface AlbumData {
  albumId: string;
  title: string;
  artist: string;
  artwork: string;
  tracks: Track[];
  experienceUrl: string | null;
}

export default function ExperienceBuilder() {
  const { albumId } = useParams<{ albumId: string }>();
  const [album, setAlbum] = useState<AlbumData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [experience, setExperience] = useState<ExperienceJSON>(createEmptyExperience());
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [showSource, setShowSource] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [currentLayoutId, setCurrentLayoutId] = useState<string>('default');

  // Timeline state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [previewTrackId, setPreviewTrackId] = useState<number>(1);
  const [timelineDuration, setTimelineDuration] = useState(30); // Default 30 seconds
  const [selectedKeyframeTime, setSelectedKeyframeTime] = useState<number | null>(null);
  const [showKeyframePanel, setShowKeyframePanel] = useState(false);
  const [selectedStateId, setSelectedStateId] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set(['position', 'size']));
  const [canvasScrollY, setCanvasScrollY] = useState(0);
  const selectedStateIdRef = useRef<string | null>(null);
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({});
  const [activeGuides, setActiveGuides] = useState<SnapGuidesType>({ vertical: [], horizontal: [] });
  // Track raw (unsnapped) position during drag to allow "breaking free" from snaps
  const rawDragPositionRef = useRef<{ x: number; y: number } | null>(null);
  // Track last used font for new text elements
  const lastUsedFontRef = useRef<FontFamily | undefined>(undefined);
  const animationFrameRef = useRef<number | null>(null);

  // Device preview presets - matches mobile app's scale calculation: screenWidth / canvas.width
  const DEVICE_PRESETS = {
    'iphone-se': { name: 'iPhone SE', width: 375, height: 667 },
    'iphone-14': { name: 'iPhone 14', width: 390, height: 844 },
    'iphone-15-pro': { name: 'iPhone 15 Pro', width: 393, height: 852 },
    'iphone-15-pro-max': { name: 'iPhone 15 Pro Max', width: 430, height: 932 },
    'pixel-7': { name: 'Pixel 7', width: 412, height: 915 },
  } as const;
  type DeviceId = keyof typeof DEVICE_PRESETS;
  const [previewDevice, setPreviewDevice] = useState<DeviceId>('iphone-15-pro');

  // Fixed phone frame scale - preview scales naturally with browser zoom like the rest of the UI
  const PHONE_FRAME_SCALE = 0.65;

  // Preview zoom - allows user to zoom in/out on the phone preview without affecting pixel accuracy
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [showAiDocsModal, setShowAiDocsModal] = useState(false);
  const [copiedAiDocs, setCopiedAiDocs] = useState(false);
  const ZOOM_PRESETS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

  // Keep ref in sync with state to avoid stale closure issues
  useEffect(() => {
    selectedStateIdRef.current = selectedStateId;
  }, [selectedStateId]);
  const lastTimeRef = useRef<number>(0);

  // Get all layout IDs and current layout
  const layoutIds = Object.keys(experience.layouts);
  const currentLayout = experience.layouts[currentLayoutId] || experience.layouts.default;
  const elements = currentLayout.elements;

  // Calculate preview scale - simulates how mobile app scales canvas to fit device
  const devicePreset = DEVICE_PRESETS[previewDevice];
  const canvasWidth = currentLayout?.canvas?.width || 375;
  const previewScale = devicePreset.width / canvasWidth;

  // Find selected element
  const selectedElement = selectedElementId
    ? elements.find(el => el.id === selectedElementId)
    : null;

  // Get elements that have keyframes for the current track
  const elementsWithKeyframes = useMemo(() => {
    return elements
      .filter(el => {
        const kfs = el.keyframes?.[previewTrackId];
        return kfs && kfs.length > 0;
      })
      .map(el => ({
        element: el,
        keyframes: [...(el.keyframes?.[previewTrackId] || [])].sort((a, b) => a.time - b.time),
      }));
  }, [elements, previewTrackId]);

  // Compute effective style for selected element based on editing context
  // Two-axis model: keyframe (from timeline) takes priority, then variable state (from tabs)
  const selectedElementEffectiveStyle = useMemo((): Partial<ElementStyle> => {
    if (!selectedElement) {
      return {};
    }

    const baseStyle = selectedElement.style || {};

    // If a keyframe time is selected (from timeline), show that keyframe's style
    if (selectedKeyframeTime !== null) {
      const elementKeyframes = selectedElement.keyframes?.[previewTrackId] || [];
      const keyframe = elementKeyframes.find(kf => kf.time === selectedKeyframeTime);
      if (keyframe?.style) {
        return { ...baseStyle, ...keyframe.style };
      }
      return baseStyle;
    }

    // If a variable state is selected (from tabs), show that state's style
    if (selectedStateId) {
      const states = selectedElement.states || [];
      const state = states.find(s => s.id === selectedStateId);
      if (state?.style) {
        return { ...baseStyle, ...state.style };
      }
      return baseStyle;
    }

    // No keyframe or state selected - return base style
    return baseStyle;
  }, [selectedElement, selectedKeyframeTime, previewTrackId, selectedStateId]);

  // Check for trigger conflicts - find another layout with the same trigger
  const getTriggerConflict = (layoutId: string): string | null => {
    const layout = experience.layouts[layoutId];
    const trigger = layout?.trigger;
    if (!trigger) return null;

    for (const [otherId, otherLayout] of Object.entries(experience.layouts)) {
      if (otherId === layoutId) continue;
      const otherTrigger = otherLayout.trigger;
      if (!otherTrigger) continue;

      // Check for matching triggers
      if (trigger.event === 'trackStart' && otherTrigger.event === 'trackStart') {
        if (trigger.trackId === otherTrigger.trackId) {
          return otherId;
        }
      } else if (trigger.event === 'playbackPosition' && otherTrigger.event === 'playbackPosition') {
        if (trigger.time === otherTrigger.time) {
          return otherId;
        }
      }
    }
    return null;
  };

  const triggerConflict = currentLayoutId !== 'default' ? getTriggerConflict(currentLayoutId) : null;

  useEffect(() => {
    if (!albumId) {
      setError('No album ID provided');
      setLoading(false);
      return;
    }

    const fetchAlbum = async () => {
      setLoading(true);
      try {
        // Use relative URL in dev, production URL in prod
        const baseUrl = process.env.NODE_ENV === 'development'
          ? 'http://localhost:3001'
          : 'https://api.album.cards';

        const response = await fetch(`${baseUrl}/album/${albumId}/builder`);

        if (!response.ok) {
          throw new Error('Album not found');
        }

        const data = await response.json();
        setAlbum(data);

        // If album has an existing experience, load it
        if (data.experienceUrl) {
          try {
            const expResponse = await fetch(data.experienceUrl, { cache: 'no-store' });
            if (expResponse.ok) {
              const expData = await expResponse.json();
              // Migrate legacy data to states
              const migratedData = migrateToStates(expData);
              setExperience(migratedData);
            }
          } catch (expErr) {
            console.warn('Failed to load existing experience:', expErr);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load album');
      } finally {
        setLoading(false);
      }
    };

    fetchAlbum();
  }, [albumId]);

  // Timeline animation loop
  useEffect(() => {
    if (!isPlaying) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    lastTimeRef.current = performance.now();

    const animate = (currentTime: number) => {
      const deltaTime = (currentTime - lastTimeRef.current) / 1000;
      lastTimeRef.current = currentTime;

      setPlaybackTime(prev => {
        const newTime = prev + deltaTime;
        if (newTime >= timelineDuration) {
          return 0; // Loop back to start
        }
        return newTime;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, timelineDuration]);

  // Update timeline duration when track changes
  useEffect(() => {
    if (!album?.tracks) return;
    const track = album.tracks[previewTrackId - 1];
    if (track?.duration) {
      const seconds = parseDurationToSeconds(track.duration);
      if (seconds > 0) {
        setTimelineDuration(seconds);
        // Reset playback if current time exceeds new duration
        setPlaybackTime(prev => prev > seconds ? 0 : prev);
      }
    }
  }, [previewTrackId, album]);

  // Add a keyframe to the selected element at the current playback time
  const handleAddKeyframe = useCallback((interpolate: boolean) => {
    if (!selectedElementId) return;

    const time = Math.round(playbackTime * 10) / 10;

    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      const elementIndex = layout.elements.findIndex(el => el.id === selectedElementId);
      if (elementIndex === -1) return prev;

      const element = layout.elements[elementIndex];
      const existingKeyframes = element.keyframes?.[previewTrackId] || [];

      // Check for time collision
      if (existingKeyframes.some(kf => kf.time === time)) {
        return prev; // Already exists, just select it (done after)
      }

      const newKeyframe: ElementKeyframe = { time, interpolate };
      const newKeyframes = [...existingKeyframes, newKeyframe].sort((a, b) => a.time - b.time);

      const newElements = [...layout.elements];
      newElements[elementIndex] = {
        ...element,
        keyframes: {
          ...element.keyframes,
          [previewTrackId]: newKeyframes,
        },
      };

      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: newElements,
          },
        },
      };
    });

    setSelectedKeyframeTime(time);
  }, [selectedElementId, playbackTime, currentLayoutId, previewTrackId]);

  // Delete a keyframe from a specific element
  const handleDeleteKeyframe = useCallback((elementId: string, time: number) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      const elementIndex = layout.elements.findIndex(el => el.id === elementId);
      if (elementIndex === -1) return prev;

      const element = layout.elements[elementIndex];
      const existingKeyframes = element.keyframes?.[previewTrackId] || [];
      const newKeyframes = existingKeyframes.filter(kf => kf.time !== time);

      const newElements = [...layout.elements];
      newElements[elementIndex] = {
        ...element,
        keyframes: {
          ...element.keyframes,
          [previewTrackId]: newKeyframes,
        },
      };

      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: newElements,
          },
        },
      };
    });

    // Clear selection if we deleted the selected keyframe
    if (selectedKeyframeTime === time && selectedElementId === elementId) {
      setSelectedKeyframeTime(null);
    }
  }, [currentLayoutId, previewTrackId, selectedKeyframeTime, selectedElementId]);

  // Update or create text for keyframe (used by inline canvas text editing)
  const handleUpdateTextAtKeyframe = useCallback((elementId: string, text: string) => {
    if (selectedKeyframeTime === null) return;

    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;

      const elementIndex = layout.elements.findIndex(el => el.id === elementId);
      if (elementIndex === -1) return prev;

      const element = layout.elements[elementIndex];
      const keyframes = element.keyframes?.[previewTrackId] || [];
      const kfIndex = findKeyframeIndexAtTime(keyframes, selectedKeyframeTime);

      if (kfIndex === -1) return prev; // No keyframe at this time

      const newKeyframes = [...keyframes];
      newKeyframes[kfIndex] = {
        ...newKeyframes[kfIndex],
        text,
      };

      const newElements = [...layout.elements];
      newElements[elementIndex] = {
        ...element,
        keyframes: {
          ...element.keyframes,
          [previewTrackId]: newKeyframes,
        },
      };

      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: newElements,
          },
        },
      };
    });
  }, [currentLayoutId, selectedKeyframeTime, previewTrackId]);

  // Handle updating element style properties (base style)
  const handleUpdateStyle = useCallback((elementId: string, styleUpdates: Partial<ExperienceElement['style']>) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId) return el;
              return {
                ...el,
                style: { ...el.style, ...styleUpdates },
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Helper to compute effective position including interpolation
  const getEffectivePosition = useCallback((element: ExperienceElement): { x: number; y: number } => {
    const baseX = element.style?.x ?? 0;
    const baseY = element.style?.y ?? 0;
    const keyframes = element.keyframes?.[previewTrackId] || [];

    // If a keyframe is selected, use that keyframe's style
    if (selectedKeyframeTime !== null) {
      const kf = keyframes.find(k => k.time === selectedKeyframeTime);
      if (kf?.style) {
        return {
          x: kf.style.x ?? baseX,
          y: kf.style.y ?? baseY,
        };
      }
    }

    // Fall back to base style (interpolation handled by CanvasElement)
    return { x: baseX, y: baseY };
  }, [selectedKeyframeTime, previewTrackId]);

  // Handle moving an element by updating its position
  const handleMoveElement = useCallback((elementId: string, dx: number, dy: number) => {
    // Find the element to get its current effective position
    const layout = experience.layouts[currentLayoutId];
    if (!layout) return;
    const element = layout.elements.find(el => el.id === elementId);
    if (!element) return;

    // Get the current effective position
    let currentX = element.style?.x ?? 0;
    let currentY = element.style?.y ?? 0;

    // Get position from the appropriate context
    const variableStateId = selectedStateIdRef.current;
    if (elementId === selectedElementId) {
      if (selectedKeyframeTime !== null || !variableStateId) {
        // Use effective position from keyframe or base
        const effectivePos = getEffectivePosition(element);
        currentX = effectivePos.x;
        currentY = effectivePos.y;
      } else if (variableStateId) {
        // Check variable state
        const states = element.states || [];
        const varState = states.find(s => s.id === variableStateId);
        if (varState?.style) {
          currentX = varState.style.x ?? currentX;
          currentY = varState.style.y ?? currentY;
        }
      }
    }

    // Track raw (unsnapped) position to allow breaking free from snaps
    // Initialize on first drag frame, then accumulate deltas
    if (!rawDragPositionRef.current) {
      rawDragPositionRef.current = { x: currentX, y: currentY };
    }
    rawDragPositionRef.current.x += dx;
    rawDragPositionRef.current.y += dy;

    // Use raw position for snap calculation (not the already-snapped position)
    const rawX = rawDragPositionRef.current.x;
    const rawY = rawDragPositionRef.current.y;

    // Get element dimensions for snapping
    const elementWidth = element.style?.width ?? 100;
    const elementHeight = element.style?.height ?? 100;

    // Get other elements' bounds for snapping
    const otherElements = layout.elements
      .filter(el => el.id !== elementId)
      .map(el => ({
        x: el.style?.x ?? 0,
        y: el.style?.y ?? 0,
        width: el.style?.width ?? 100,
        height: el.style?.height ?? 100,
      }));

    // Calculate snap using raw position
    const canvasSize = {
      width: currentLayout.canvas.width,
      height: currentLayout.canvas.height,
    };
    const snapResult = calculateSnap(
      { x: rawX, y: rawY, width: elementWidth, height: elementHeight },
      otherElements,
      canvasSize
    );

    const newX = snapResult.x;
    const newY = snapResult.y;

    // Update active guides for visual feedback
    setActiveGuides(snapResult.guides);

    // If editing a keyframe, update keyframe style
    if (selectedKeyframeTime !== null && elementId === selectedElementId) {
      setExperience(prev => {
        const layout = prev.layouts[currentLayoutId];
        const elementIndex = layout.elements.findIndex(el => el.id === elementId);
        if (elementIndex === -1) return prev;

        const element = layout.elements[elementIndex];
        const keyframes = element.keyframes?.[previewTrackId] || [];
        const kfIndex = findKeyframeIndexAtTime(keyframes, selectedKeyframeTime);

        if (kfIndex === -1) return prev;

        const newKeyframes = [...keyframes];
        newKeyframes[kfIndex] = {
          ...newKeyframes[kfIndex],
          style: {
            ...newKeyframes[kfIndex].style,
            x: newX,
            y: newY,
          },
        };

        const newElements = [...layout.elements];
        newElements[elementIndex] = {
          ...element,
          keyframes: {
            ...element.keyframes,
            [previewTrackId]: newKeyframes,
          },
        };

        return {
          ...prev,
          layouts: {
            ...prev.layouts,
            [currentLayoutId]: {
              ...layout,
              elements: newElements,
            },
          },
        };
      });
      return;
    }

    // Route to variable state or base style
    if (variableStateId) {
      setExperience(prev => {
        const layout = prev.layouts[currentLayoutId];
        if (!layout) return prev;
        return {
          ...prev,
          layouts: {
            ...prev.layouts,
            [currentLayoutId]: {
              ...layout,
              elements: layout.elements.map(el => {
                if (el.id !== elementId || !el.states) return el;
                return {
                  ...el,
                  states: el.states.map(s => {
                    if (s.id !== variableStateId) return s;
                    return { ...s, style: { ...s.style, x: newX, y: newY } };
                  }),
                } as ExperienceElement;
              }),
            },
          },
        };
      });
    } else {
      handleUpdateStyle(elementId, { x: newX, y: newY });
    }
  }, [currentLayoutId, currentLayout, experience, selectedElementId, selectedKeyframeTime, previewTrackId, handleUpdateStyle, getEffectivePosition]);

  // Handle resizing an element
  const handleResizeElement = useCallback((elementId: string, width: number, height: number) => {
    const variableStateId = selectedStateIdRef.current;

    // If editing a keyframe, update keyframe style
    if (selectedKeyframeTime !== null && elementId === selectedElementId) {
      setExperience(prev => {
        const layout = prev.layouts[currentLayoutId];
        const elementIndex = layout.elements.findIndex(el => el.id === elementId);
        if (elementIndex === -1) return prev;

        const element = layout.elements[elementIndex];
        const keyframes = element.keyframes?.[previewTrackId] || [];
        const kfIndex = findKeyframeIndexAtTime(keyframes, selectedKeyframeTime);

        if (kfIndex === -1) return prev;

        const newKeyframes = [...keyframes];
        newKeyframes[kfIndex] = {
          ...newKeyframes[kfIndex],
          style: {
            ...newKeyframes[kfIndex].style,
            width,
            height,
          },
        };

        const newElements = [...layout.elements];
        newElements[elementIndex] = {
          ...element,
          keyframes: {
            ...element.keyframes,
            [previewTrackId]: newKeyframes,
          },
        };

        return {
          ...prev,
          layouts: {
            ...prev.layouts,
            [currentLayoutId]: {
              ...layout,
              elements: newElements,
            },
          },
        };
      });
      return;
    }

    // Route to variable state or base style
    if (variableStateId) {
      setExperience(prev => {
        const layout = prev.layouts[currentLayoutId];
        if (!layout) return prev;
        return {
          ...prev,
          layouts: {
            ...prev.layouts,
            [currentLayoutId]: {
              ...layout,
              elements: layout.elements.map(el => {
                if (el.id !== elementId || !el.states) return el;
                return {
                  ...el,
                  states: el.states.map(s => {
                    if (s.id !== variableStateId) return s;
                    return { ...s, style: { ...s.style, width, height } };
                  }),
                } as ExperienceElement;
              }),
            },
          },
        };
      });
    } else {
      handleUpdateStyle(elementId, { width, height });
    }
  }, [selectedKeyframeTime, selectedElementId, currentLayoutId, previewTrackId, handleUpdateStyle]);

  // Handle proportional scaling of an element (width, height, and fontSize)
  const handleScaleElement = useCallback((elementId: string, width: number, height: number, fontSize: number) => {
    const variableStateId = selectedStateIdRef.current;

    // If editing a keyframe, update keyframe style
    if (selectedKeyframeTime !== null && elementId === selectedElementId) {
      setExperience(prev => {
        const layout = prev.layouts[currentLayoutId];
        const elementIndex = layout.elements.findIndex(el => el.id === elementId);
        if (elementIndex === -1) return prev;

        const element = layout.elements[elementIndex];
        const keyframes = element.keyframes?.[previewTrackId] || [];
        const kfIndex = findKeyframeIndexAtTime(keyframes, selectedKeyframeTime);

        if (kfIndex === -1) return prev;

        const newKeyframes = [...keyframes];
        newKeyframes[kfIndex] = {
          ...newKeyframes[kfIndex],
          style: {
            ...newKeyframes[kfIndex].style,
            width,
            height,
            fontSize,
          },
        };

        const newElements = [...layout.elements];
        newElements[elementIndex] = {
          ...element,
          keyframes: {
            ...element.keyframes,
            [previewTrackId]: newKeyframes,
          },
        };

        return {
          ...prev,
          layouts: {
            ...prev.layouts,
            [currentLayoutId]: {
              ...layout,
              elements: newElements,
            },
          },
        };
      });
      return;
    }

    // Route to variable state or base style
    if (variableStateId) {
      setExperience(prev => {
        const layout = prev.layouts[currentLayoutId];
        if (!layout) return prev;
        return {
          ...prev,
          layouts: {
            ...prev.layouts,
            [currentLayoutId]: {
              ...layout,
              elements: layout.elements.map(el => {
                if (el.id !== elementId || !el.states) return el;
                return {
                  ...el,
                  states: el.states.map(s => {
                    if (s.id !== variableStateId) return s;
                    return { ...s, style: { ...s.style, width, height, fontSize } };
                  }),
                } as ExperienceElement;
              }),
            },
          },
        };
      });
    } else {
      handleUpdateStyle(elementId, { width, height, fontSize });
    }
  }, [selectedKeyframeTime, selectedElementId, currentLayoutId, previewTrackId, handleUpdateStyle]);

  // Handle updating element properties (id, text, src)
  const handleUpdateElement = useCallback((elementId: string, updates: Partial<ExperienceElement>) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId) return el;
              return { ...el, ...updates } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Smart property update - routes to keyframe, variable state, or base style
  // Priority: keyframe > variable state > base
  const handleSmartStyleUpdate = useCallback((elementId: string, styleUpdates: Partial<ExperienceElement['style']>) => {
    // Track last used font for new text elements
    if (styleUpdates?.fontFamily) {
      lastUsedFontRef.current = styleUpdates.fontFamily;
    }

    const variableStateId = selectedStateIdRef.current;

    // If a keyframe is selected (from timeline), update keyframe style
    if (selectedKeyframeTime !== null) {
      setExperience(prev => {
        const layout = prev.layouts[currentLayoutId];
        const elementIndex = layout.elements.findIndex(el => el.id === elementId);
        if (elementIndex === -1) return prev;

        const element = layout.elements[elementIndex];
        const keyframes = element.keyframes?.[previewTrackId] || [];
        const kfIndex = findKeyframeIndexAtTime(keyframes, selectedKeyframeTime);

        if (kfIndex === -1) return prev;

        const newKeyframes = [...keyframes];
        newKeyframes[kfIndex] = {
          ...newKeyframes[kfIndex],
          style: {
            ...newKeyframes[kfIndex].style,
            ...styleUpdates,
          },
        };

        const newElements = [...layout.elements];
        newElements[elementIndex] = {
          ...element,
          keyframes: {
            ...element.keyframes,
            [previewTrackId]: newKeyframes,
          },
        };

        return {
          ...prev,
          layouts: {
            ...prev.layouts,
            [currentLayoutId]: {
              ...layout,
              elements: newElements,
            },
          },
        };
      });
      return;
    }

    // Route to variable state or base style
    if (variableStateId) {
      setExperience(prev => {
        const layout = prev.layouts[currentLayoutId];
        if (!layout) return prev;
        return {
          ...prev,
          layouts: {
            ...prev.layouts,
            [currentLayoutId]: {
              ...layout,
              elements: layout.elements.map(el => {
                if (el.id !== elementId || !el.states) return el;
                return {
                  ...el,
                  states: el.states.map(s => {
                    if (s.id !== variableStateId) return s;
                    return { ...s, style: { ...s.style, ...styleUpdates } };
                  }),
                } as ExperienceElement;
              }),
            },
          },
        };
      });
    } else {
      // No keyframe or state selected - update base style
      handleUpdateStyle(elementId, styleUpdates);
    }
  }, [selectedKeyframeTime, previewTrackId, currentLayoutId, handleUpdateStyle]);

  // Smart text update - routes to keyframe, variable state, or base text
  const handleSmartTextUpdate = useCallback((elementId: string, text: string) => {
    const variableStateId = selectedStateIdRef.current;

    // If a keyframe is selected (from timeline), update keyframe text
    if (selectedKeyframeTime !== null) {
      setExperience(prev => {
        const layout = prev.layouts[currentLayoutId];
        const elementIndex = layout.elements.findIndex(el => el.id === elementId);
        if (elementIndex === -1) return prev;

        const element = layout.elements[elementIndex];
        const keyframes = element.keyframes?.[previewTrackId] || [];
        const kfIndex = findKeyframeIndexAtTime(keyframes, selectedKeyframeTime);

        if (kfIndex === -1) return prev;

        const newKeyframes = [...keyframes];
        newKeyframes[kfIndex] = {
          ...newKeyframes[kfIndex],
          text,
        };

        const newElements = [...layout.elements];
        newElements[elementIndex] = {
          ...element,
          keyframes: {
            ...element.keyframes,
            [previewTrackId]: newKeyframes,
          },
        };

        return {
          ...prev,
          layouts: {
            ...prev.layouts,
            [currentLayoutId]: {
              ...layout,
              elements: newElements,
            },
          },
        };
      });
      return;
    }

    // Route to variable state or base text
    if (variableStateId) {
      setExperience(prev => {
        const layout = prev.layouts[currentLayoutId];
        if (!layout) return prev;
        return {
          ...prev,
          layouts: {
            ...prev.layouts,
            [currentLayoutId]: {
              ...layout,
              elements: layout.elements.map(el => {
                if (el.id !== elementId || !el.states) return el;
                return {
                  ...el,
                  states: el.states.map(s => {
                    if (s.id !== variableStateId) return s;
                    return { ...s, text };
                  }),
                } as ExperienceElement;
              }),
            },
          },
        };
      });
    } else {
      // No keyframe or state selected - update base text
      handleUpdateElement(elementId, { text });
    }
  }, [selectedKeyframeTime, previewTrackId, currentLayoutId, handleUpdateElement]);

  // Handle deleting an element
  const handleDeleteElement = useCallback((elementId: string) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.filter(el => el.id !== elementId),
          },
        },
      };
    });
    setSelectedElementId(null);
  }, [currentLayoutId]);

  // Handle reordering elements (for z-index)
  const handleReorderElements = useCallback((fromIndex: number, toIndex: number) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      const newElements = [...layout.elements];
      const [removed] = newElements.splice(fromIndex, 1);
      newElements.splice(toIndex, 0, removed);
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: newElements,
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle adding a new state to an element
  const handleAddElementState = useCallback((elementId: string, providedState?: ElementState) => {
    const newState = providedState || createVariableState('', '');
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId) return el;
              return {
                ...el,
                states: [...(el.states || []), newState],
              } as ExperienceElement;
            }),
          },
        },
      };
    });
    setSelectedStateId(newState.id);
  }, [currentLayoutId]);

  // Note: Keyframe selection is now independent of state tabs
  // Keyframes are selected via timeline, states via tabs
  // The handleSmartStyleUpdate combines both contexts when editing

  // Handle removing a state from an element
  const handleRemoveElementState = useCallback((elementId: string, stateId: string) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId || !el.states) return el;
              const newStates = el.states.filter(s => s.id !== stateId);
              return {
                ...el,
                states: newStates.length > 0 ? newStates : undefined,
              } as ExperienceElement;
            }),
          },
        },
      };
    });
    if (selectedStateId === stateId) {
      setSelectedStateId(null);
    }
  }, [currentLayoutId, selectedStateId]);

  // Handle updating a state
  const handleUpdateElementState = useCallback((elementId: string, stateId: string, updates: Partial<ElementState>) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId || !el.states) return el;
              return {
                ...el,
                states: el.states.map(s =>
                  s.id === stateId ? { ...s, ...updates } : s
                ),
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle adding a new variable
  const handleAddVariable = useCallback((name: string, defaultValue: string = '') => {
    setExperience(prev => ({
      ...prev,
      variables: [...(prev.variables || []), { name, defaultValue }],
    }));
  }, []);

  // Handle removing a variable
  const handleRemoveVariable = useCallback((variableName: string) => {
    setExperience(prev => ({
      ...prev,
      variables: (prev.variables || []).filter(v => v.name !== variableName),
    }));
  }, []);

  // Get all available variables from the experience
  const availableVariables = useMemo(() => {
    return experience.variables || [];
  }, [experience.variables]);

  // Get states for the selected element
  const selectedElementStates = useMemo(() => {
    if (!selectedElement) return [];
    return selectedElement.states || [];
  }, [selectedElement]);

  // All states are now variable-based (keyframes are per-element, not state-based)
  const selectedElementVariableStates = selectedElementStates;

  // Handle adding a button action
  const handleAddButtonAction = useCallback((elementId: string) => {
    const newAction: ButtonAction = {
      type: 'setVariable',
      variableName: '',
      value: '',
    };
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId || el.type !== 'button') return el;
              const btnEl = el as { actions?: ButtonAction[] } & typeof el;
              return {
                ...btnEl,
                actions: [...(btnEl.actions || []), newAction],
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle updating a button action
  const handleUpdateButtonAction = useCallback((elementId: string, actionIndex: number, updates: Partial<ButtonAction>) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId || el.type !== 'button') return el;
              const btnEl = el as { actions?: ButtonAction[] } & typeof el;
              if (!btnEl.actions) return el;
              return {
                ...btnEl,
                actions: btnEl.actions.map((action, i) =>
                  i === actionIndex ? { ...action, ...updates } as ButtonAction : action
                ),
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle removing a button action
  const handleRemoveButtonAction = useCallback((elementId: string, actionIndex: number) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId || el.type !== 'button') return el;
              const btnEl = el as { actions?: ButtonAction[] } & typeof el;
              if (!btnEl.actions) return el;
              const newActions = btnEl.actions.filter((_, i) => i !== actionIndex);
              return {
                ...btnEl,
                actions: newActions.length > 0 ? newActions : undefined,
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle updating canvas properties
  const handleUpdateCanvas = useCallback((updates: Partial<Canvas>) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            canvas: { ...layout.canvas, ...updates },
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle adding an animation to an element
  const handleAddAnimation = useCallback((elementId: string) => {
    const newAnimation: Animation = {
      property: 'opacity',
      keyframes: [
        { at: 0, value: 1 },
        { at: 1, value: 0 },
      ],
    };
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId) return el;
              return {
                ...el,
                animations: [...(el.animations || []), newAnimation],
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle updating an animation
  const handleUpdateAnimation = useCallback((elementId: string, animIndex: number, updates: Partial<Animation>) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId || !el.animations) return el;
              return {
                ...el,
                animations: el.animations.map((anim, i) =>
                  i === animIndex ? { ...anim, ...updates } : anim
                ),
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle removing an animation
  const handleRemoveAnimation = useCallback((elementId: string, animIndex: number) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId || !el.animations) return el;
              return {
                ...el,
                animations: el.animations.filter((_, i) => i !== animIndex),
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle updating a keyframe
  const handleUpdateKeyframe = useCallback((elementId: string, animIndex: number, keyframeIndex: number, updates: Partial<Keyframe>) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId || !el.animations) return el;
              return {
                ...el,
                animations: el.animations.map((anim, ai) => {
                  if (ai !== animIndex) return anim;
                  return {
                    ...anim,
                    keyframes: anim.keyframes.map((kf, ki) =>
                      ki === keyframeIndex ? { ...kf, ...updates } : kf
                    ),
                  };
                }),
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle adding an animation keyframe
  const handleAddAnimationKeyframe = useCallback((elementId: string, animIndex: number) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId || !el.animations) return el;
              return {
                ...el,
                animations: el.animations.map((anim, ai) => {
                  if (ai !== animIndex) return anim;
                  const lastKf = anim.keyframes[anim.keyframes.length - 1];
                  return {
                    ...anim,
                    keyframes: [...anim.keyframes, { at: (lastKf?.at || 0) + 1, value: lastKf?.value || 0 }],
                  };
                }),
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle removing a keyframe
  const handleRemoveKeyframe = useCallback((elementId: string, animIndex: number, keyframeIndex: number) => {
    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: layout.elements.map(el => {
              if (el.id !== elementId || !el.animations) return el;
              return {
                ...el,
                animations: el.animations.map((anim, ai) => {
                  if (ai !== animIndex) return anim;
                  return {
                    ...anim,
                    keyframes: anim.keyframes.filter((_, ki) => ki !== keyframeIndex),
                  };
                }),
              } as ExperienceElement;
            }),
          },
        },
      };
    });
  }, [currentLayoutId]);

  // Handle creating a new layout
  const handleCreateLayout = useCallback(() => {
    const name = prompt('Enter layout name:');
    if (!name || name.trim() === '') return;

    const layoutId = name.trim().toLowerCase().replace(/\s+/g, '-');
    if (experience.layouts[layoutId]) {
      alert('A layout with this name already exists');
      return;
    }

    setExperience(prev => ({
      ...prev,
      layouts: {
        ...prev.layouts,
        [layoutId]: {
          canvas: {
            width: 375,
            height: 875,
            backgroundColor: '#FFFFFF',
          },
          elements: [],
        },
      },
    }));
    setCurrentLayoutId(layoutId);
    setSelectedElementId(null);
  }, [experience.layouts]);

  // Handle deleting a layout
  const handleDeleteLayout = useCallback((layoutId: string) => {
    if (layoutId === 'default') {
      alert('Cannot delete the default layout');
      return;
    }
    if (!window.confirm(`Delete layout "${layoutId}"?`)) return;

    setExperience(prev => {
      const newLayouts = { ...prev.layouts };
      delete newLayouts[layoutId];
      return {
        ...prev,
        layouts: newLayouts as ExperienceJSON['layouts'],
      };
    });
    setCurrentLayoutId('default');
    setSelectedElementId(null);
  }, []);

  // Handle updating a layout's trigger
  const handleUpdateLayoutTrigger = useCallback((layoutId: string, trigger: LayoutTrigger | undefined) => {
    setExperience(prev => {
      const layout = prev.layouts[layoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [layoutId]: {
            ...layout,
            trigger,
          },
        },
      };
    });
  }, []);

  // Handle clicking on the canvas background to deselect
  const handleCanvasClick = () => {
    setSelectedElementId(null);
  };

  // Handle adding a new element
  const handleAddElement = useCallback((type: 'text' | 'image' | 'view' | 'button' | 'video') => {
    // Generate simple incremental ID: text, text-1, text-2, etc.
    const allElements = Object.values(experience.layouts).flatMap(layout => layout.elements);
    const existingIds = new Set(allElements.map(el => el.id));

    let id: string = type;
    let counter = 1;
    while (existingIds.has(id)) {
      id = `${type}-${counter}`;
      counter++;
    }

    let newElement: ExperienceElement;

    switch (type) {
      case 'text':
        newElement = {
          id,
          type: 'text',
          text: 'Double click to edit',
          style: {
            x: 50,
            y: 50,
            fontSize: 16,
            color: '#000000',
            ...(lastUsedFontRef.current ? { fontFamily: lastUsedFontRef.current } : {}),
          },
        };
        break;
      case 'image':
        newElement = {
          id,
          type: 'image',
          src: 'https://www.wolflair.com/wp-content/uploads/2017/01/placeholder.jpg',
          style: { x: 50, y: 50, width: 150, height: 150 },
        } as any;
        break;
      case 'view':
        newElement = {
          id,
          type: 'view',
          style: { x: 50, y: 50, width: 200, height: 100, backgroundColor: '#eeeeee' },
        } as any;
        break;
      case 'button':
        newElement = {
          id,
          type: 'button',
          text: 'Button',
          action: 'navigateToLayout',
          targetLayout: 'default',
          style: { x: 50, y: 50, width: 120, height: 40 },
        } as any;
        break;
      case 'video':
        newElement = {
          id,
          type: 'video',
          src: '',
          muted: true,
          loop: false,
          style: { x: 50, y: 50, width: 300, height: 200 },
        } as any;
        break;
    }

    setExperience(prev => {
      const layout = prev.layouts[currentLayoutId];
      if (!layout) return prev;
      return {
        ...prev,
        layouts: {
          ...prev.layouts,
          [currentLayoutId]: {
            ...layout,
            elements: [...layout.elements, newElement],
          },
        },
      };
    });

    setSelectedElementId(id);
    setShowAddMenu(false);
  }, [currentLayoutId, experience.layouts]);

  // Handle saving experience to Supabase
  const handleSave = useCallback(async () => {
    if (!albumId) return;

    setSaving(true);
    try {
      const baseUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:3001'
        : 'https://api.album.cards';

      const response = await fetch(`${baseUrl}/album/${albumId}/experience`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(experience),
      });

      if (!response.ok) {
        throw new Error('Failed to save');
      }

      await response.json();
      alert('Experience saved successfully!');
    } catch (err) {
      alert('Failed to save experience');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }, [albumId, experience]);

  return (
    <div className="experience-builder">
      <nav className="eb-navbar">
        <div className="eb-navbar-left">
          <Link to="/" className="nav-btn">
            <Home size={18} />
          </Link>
          <span className="eb-navbar-title">Experience Builder</span>
          {album && (
            <span className="eb-album-name">{album.title} — {album.artist}</span>
          )}
        </div>
        <div className="eb-navbar-right">
          <button
            type="button"
            className="nav-btn"
            onClick={() => setShowAiDocsModal(true)}
            title="Copy reference docs to paste into an AI"
          >
            <span>AI docs</span>
          </button>
          <button className="nav-btn" onClick={() => setShowSource(!showSource)}>
            {showSource ? <><Layers size={18} /><span>Visual</span></> : <><Code size={18} /><span>Source</span></>}
          </button>
          <button className="nav-btn primary" onClick={handleSave} disabled={saving}>
            <Save size={18} />
            <span>{saving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </nav>

      {showSource ? (
        <div className="eb-source-view">
          <CodeEditor
            value={JSON.stringify(experience, null, 2)}
            language="json"
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setExperience(parsed);
                setSourceError(null);
              } catch (err) {
                setSourceError('Invalid JSON');
              }
            }}
            padding={20}
            data-color-mode="dark"
            style={{
              flex: 1,
              fontSize: 13,
              fontFamily: 'Monaco, Menlo, Consolas, monospace',
              backgroundColor: '#0d0d0d',
              overflow: 'auto',
            }}
          />
          {sourceError && <div className="eb-source-error">{sourceError}</div>}
        </div>
      ) : (
      <div className="eb-main">
        <Sidebar
          layoutIds={layoutIds}
          currentLayoutId={currentLayoutId}
          experience={experience}
          getTriggerConflict={getTriggerConflict}
          setCurrentLayoutId={setCurrentLayoutId}
          setSelectedElementId={setSelectedElementId}
          handleCreateLayout={handleCreateLayout}
          showAddMenu={showAddMenu}
          setShowAddMenu={setShowAddMenu}
          handleAddElement={handleAddElement}
          elements={elements}
          selectedElementId={selectedElementId}
          onReorderElements={handleReorderElements}
        />

        <div className="eb-canvas" onClick={() => setSelectedElementId(null)}>
          {/* Central keyframe indicator - shown when editing at a keyframe */}
          {selectedKeyframeTime !== null && (
            <div className="keyframe-mode-banner">
              <Diamond size={14} />
              <span>Keyframe Mode: {selectedKeyframeTime.toFixed(1)}s</span>
              <button
                className="keyframe-mode-exit"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedKeyframeTime(null);
                }}
              >
                Exit
              </button>
            </div>
          )}
          {/* Device preview selector and zoom controls */}
          <div className="preview-controls">
            <div className="device-selector">
              <CustomSelect
                value={previewDevice}
                onChange={(value) => setPreviewDevice(value as DeviceId)}
                options={Object.entries(DEVICE_PRESETS).map(([id, device]) => ({
                  value: id,
                  label: `${device.name} (${device.width}×${device.height})`,
                }))}
              />
              {previewScale !== 1 && (
                <span className="device-scale-info">
                  Scale: {previewScale.toFixed(2)}×
                </span>
              )}
            </div>
            <div className="preview-zoom-controls">
              <button
                className="zoom-btn"
                onClick={() => setPreviewZoom(z => Math.max(0.5, z - 0.25))}
                disabled={previewZoom <= 0.5}
              >
                −
              </button>
              <span className="zoom-value">{Math.round(previewZoom * 100)}%</span>
              <button
                className="zoom-btn"
                onClick={() => setPreviewZoom(z => Math.min(2.0, z + 0.25))}
                disabled={previewZoom >= 2.0}
              >
                +
              </button>
            </div>
          </div>
          <div className="eb-phone-wrapper">
            <div
              className="eb-phone-frame"
              style={{ transform: `scale(${PHONE_FRAME_SCALE * previewZoom})` }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="eb-phone-screen"
                style={{
                  width: devicePreset.width,
                  height: devicePreset.height,
                }}
                onScroll={(e) => setCanvasScrollY(e.currentTarget.scrollTop)}
              >
                <div
                  className="eb-canvas-inner"
                  style={{
                    width: currentLayout.canvas.width,
                    minHeight: currentLayout.canvas.height,
                    backgroundColor: currentLayout.canvas.backgroundColor || '#FFFFFF',
                    transform: `scale(${previewScale})`,
                    transformOrigin: 'top left',
                  }}
                  onClick={handleCanvasClick}
                >
                {loading && <div className="eb-loading">Loading...</div>}
                {error && <div className="eb-error">{error}</div>}
                {!loading && !error && elements.length === 0 && (
                  <div className="eb-canvas-placeholder">
                    <p>Album ID: {albumId}</p>
                    <p>Select or add layers to build your experience</p>
                  </div>
                )}
                {!loading && !error && elements.map(element => (
                  <CanvasElement
                    key={element.id}
                    element={element}
                    selected={element.id === selectedElementId}
                    onSelect={() => setSelectedElementId(element.id)}
                    onMove={(dx, dy) => handleMoveElement(element.id, dx, dy)}
                    onDragEnd={() => { rawDragPositionRef.current = null; setActiveGuides({ vertical: [], horizontal: [] }); }}
                    onResize={(width, height) => handleResizeElement(element.id, width, height)}
                    onScale={(width, height, fontSize) => handleScaleElement(element.id, width, height, fontSize)}
                    onUpdateText={(text) => handleSmartTextUpdate(element.id, text)}
                    onUpdateTextKeyframe={(text) => handleUpdateTextAtKeyframe(element.id, text)}
                    playbackTime={playbackTime}
                    previewTrackId={previewTrackId}
                    selectedKeyframeTime={selectedKeyframeTime}
                    previewVariables={previewVariables}
                    selectedStateId={selectedStateId}
                  />
                ))}
                <SnapGuides guides={activeGuides} />
                </div>
              </div>
            </div>
            {/* Scroll position indicator - always rendered to prevent layout shift */}
            <div
              className="canvas-scroll-indicator"
              style={{
                visibility: currentLayout.canvas.height * previewScale > devicePreset.height ? 'visible' : 'hidden'
              }}
            >
              <div className="scroll-track">
                <div
                  className="scroll-thumb"
                  style={{
                    height: `${(devicePreset.height / (currentLayout.canvas.height * previewScale)) * 100}%`,
                    top: `${(canvasScrollY / (currentLayout.canvas.height * previewScale - devicePreset.height)) * (100 - (devicePreset.height / (currentLayout.canvas.height * previewScale)) * 100)}%`,
                  }}
                />
              </div>
              <div className="scroll-position">{Math.round(canvasScrollY)}px</div>
            </div>
          </div>
        </div>

        <div className="eb-panel eb-properties-panel">
          {selectedElement ? (
            <>
              {/* Variable-based state tabs */}
              <div className="eb-state-tabs">
                <button
                  className={`state-tab ${!selectedStateId ? 'active' : ''}`}
                  onClick={() => setSelectedStateId(null)}
                  title="Default state"
                >
                  Default
                </button>
                {selectedElementVariableStates.map((state, index) => (
                  <button
                    key={state.id}
                    className={`state-tab ${selectedStateId === state.id ? 'active' : ''}`}
                    onClick={() => setSelectedStateId(state.id)}
                    title={state.conditions.map(c => c.type === 'trackStart' ? `Track ${c.trackId}` : (c.variable ? `${c.variable}=${c.value}` : '')).filter(Boolean).join(', ')}
                  >
                    {index + 2}
                  </button>
                ))}
                <button
                  className="state-tab add-state-tab"
                  onClick={() => handleAddElementState(selectedElement.id)}
                  title="Add Variable State"
                >
                  +
                </button>
              </div>
            </>
          ) : (
            <div className="eb-panel-header">Properties</div>
          )}
          <div className="eb-panel-content">
            {!selectedElement ? (
              <div className="properties-content">
                {/* Layout section */}
                <div className="property-section">
                  <h4>Layout: {currentLayoutId}</h4>
                  {currentLayoutId === 'default' ? (
                    <p className="trigger-hint" style={{ padding: '0 8px' }}>
                      Shown when album loads
                    </p>
                  ) : (
                    <>
                      <div className="property-row">
                        <label>Trigger</label>
                        <CustomSelect
                          value={currentLayout.trigger?.event || 'none'}
                          onChange={(value) => {
                            const event = value as LayoutTriggerEvent | 'none';
                            if (event === 'none') {
                              handleUpdateLayoutTrigger(currentLayoutId, undefined);
                            } else if (event === 'trackStart') {
                              handleUpdateLayoutTrigger(currentLayoutId, { event: 'trackStart', trackId: 1 });
                            } else if (event === 'playbackPosition') {
                              handleUpdateLayoutTrigger(currentLayoutId, { event: 'playbackPosition', time: 0 });
                            }
                          }}
                          options={[
                            { value: 'none', label: 'None' },
                            { value: 'trackStart', label: 'Track Starts' },
                            { value: 'playbackPosition', label: 'Playback Time' },
                          ]}
                        />
                      </div>
                      {currentLayout.trigger?.event === 'trackStart' && (
                        <div className="property-row">
                          <label>Track #</label>
                          <input
                            type="number"
                            min="1"
                            value={currentLayout.trigger.trackId || 1}
                            onChange={(e) => handleUpdateLayoutTrigger(
                              currentLayoutId,
                              { event: 'trackStart', trackId: parseInt(e.target.value) || 1 }
                            )}
                          />
                        </div>
                      )}
                      {currentLayout.trigger?.event === 'playbackPosition' && (
                        <div className="property-row">
                          <label>Time (s)</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={currentLayout.trigger.time || 0}
                            onChange={(e) => handleUpdateLayoutTrigger(
                              currentLayoutId,
                              { event: 'playbackPosition', time: parseFloat(e.target.value) || 0 }
                            )}
                          />
                        </div>
                      )}
                      {triggerConflict && (
                        <p className="trigger-warning">
                          Conflicts with "{triggerConflict}"
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Canvas section */}
                <div className="property-section">
                  <h4>Canvas</h4>
                  <PropertyInput
                    label="Height"
                    type="number"
                    value={currentLayout.canvas.height}
                    min={875}
                    max={10000}
                    onChange={(val) => {
                      // Allow undefined during typing - PropertyInput will clamp on blur
                      if (val !== undefined && !isNaN(Number(val))) {
                        handleUpdateCanvas({ height: Number(val) });
                      }
                    }}
                  />
                  <PropertyInput
                    label="Background"
                    type="color"
                    value={currentLayout.canvas.backgroundColor || '#ffffff'}
                    onChange={(val) => handleUpdateCanvas({ backgroundColor: String(val) })}
                  />
                </div>

                {/* Delete Layout button - only for non-default layouts */}
                {currentLayoutId !== 'default' && (
                  <div className="property-section">
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteLayout(currentLayoutId)}
                    >
                      Delete Layout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="properties-content">
                {/* Conditions section - only shown when a state is selected */}
                {selectedStateId && (() => {
                  const currentState = selectedElementStates.find(s => s.id === selectedStateId);
                  if (!currentState) return null;
                  return (
                    <div className="property-section">
                      <div className="section-header-row">
                        <h4>Conditions</h4>
                        <button
                          className="icon-btn small"
                          onClick={() => {
                            // Add a new variable condition
                            const newConditions = [...currentState.conditions, { variable: '', value: '' }];
                            handleUpdateElementState(selectedElement.id, selectedStateId, { conditions: newConditions });
                          }}
                          title="Add Condition"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      {currentState.conditions.length === 0 ? (
                        <p className="placeholder small">No conditions. This state is always active.</p>
                      ) : (
                        <div className="conditions-list">
                          {currentState.conditions.map((cond, condIndex) => (
                            <div key={condIndex} className="condition-row">
                              <CustomSelect
                                value={cond.type || 'variable'}
                                onChange={(value) => {
                                  const newConditions = [...currentState.conditions];
                                  const newType = value as 'variable' | 'trackStart';
                                  if (newType === 'trackStart') {
                                    newConditions[condIndex] = { type: 'trackStart', trackId: 1 };
                                  } else {
                                    newConditions[condIndex] = { type: 'variable', variable: '', value: '' };
                                  }
                                  handleUpdateElementState(selectedElement.id, selectedStateId, { conditions: newConditions });
                                }}
                                options={[
                                  { value: 'variable', label: 'Variable' },
                                  { value: 'trackStart', label: 'Track Start' },
                                ]}
                              />
                              {(cond.type || 'variable') === 'variable' ? (
                                <>
                                  <CustomSelect
                                    value={cond.variable || ''}
                                    onChange={(value) => {
                                      if (value === '__new__') {
                                        const name = prompt('Enter new variable name:');
                                        if (name && name.trim()) {
                                          handleAddVariable(name.trim());
                                          const newConditions = [...currentState.conditions];
                                          newConditions[condIndex] = { ...cond, variable: name.trim() };
                                          handleUpdateElementState(selectedElement.id, selectedStateId, { conditions: newConditions });
                                        }
                                      } else {
                                        const newConditions = [...currentState.conditions];
                                        newConditions[condIndex] = { ...cond, variable: value };
                                        handleUpdateElementState(selectedElement.id, selectedStateId, { conditions: newConditions });
                                      }
                                    }}
                                    options={[
                                      { value: '', label: 'Select...' },
                                      ...availableVariables.map(v => ({ value: v.name, label: v.name })),
                                      { value: '__new__', label: '+ New' },
                                    ]}
                                  />
                                  <span>=</span>
                                  <input
                                    type="text"
                                    value={cond.value || ''}
                                    placeholder="value"
                                    onChange={(e) => {
                                      const newConditions = [...currentState.conditions];
                                      newConditions[condIndex] = { ...cond, value: e.target.value };
                                      handleUpdateElementState(selectedElement.id, selectedStateId, { conditions: newConditions });
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  <span>Track #</span>
                                  <input
                                    type="number"
                                    min={1}
                                    value={cond.trackId || 1}
                                    style={{ width: 50 }}
                                    onChange={(e) => {
                                      const newConditions = [...currentState.conditions];
                                      newConditions[condIndex] = { ...cond, type: 'trackStart', trackId: parseInt(e.target.value) || 1 };
                                      handleUpdateElementState(selectedElement.id, selectedStateId, { conditions: newConditions });
                                    }}
                                  />
                                </>
                              )}
                              <button
                                className="icon-btn small danger"
                                onClick={() => {
                                  const newConditions = currentState.conditions.filter((_, i) => i !== condIndex);
                                  handleUpdateElementState(selectedElement.id, selectedStateId, { conditions: newConditions });
                                }}
                                title="Remove Condition"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Element section */}
                <div className="property-section">
                  <h4>Element</h4>
                  <div className="property-row">
                    <label>Visible</label>
                    <input
                      type="checkbox"
                      checked={selectedElementEffectiveStyle?.visible !== false}
                      onChange={(e) => handleSmartStyleUpdate(selectedElement.id, { visible: e.target.checked })}
                    />
                  </div>
                  <PropertyInput
                    label="ID"
                    value={selectedElement.id}
                    onChange={(val) => handleUpdateElement(selectedElement.id, { id: String(val) })}
                  />
                  {selectedElement.type === 'text' && (
                    <div className="property-row">
                      <label>Text</label>
                      <textarea
                        className="text-area-input"
                        value={
                          selectedStateId
                            ? (selectedElement.states?.find(s => s.id === selectedStateId)?.text ?? selectedElement.text)
                            : selectedKeyframeTime !== null
                              ? (selectedElement.keyframes?.[previewTrackId]?.find(kf => kf.time === selectedKeyframeTime)?.text ?? selectedElement.text)
                              : selectedElement.text
                        }
                        onChange={(e) => handleSmartTextUpdate(selectedElement.id, e.target.value)}
                        rows={3}
                        placeholder="Enter text..."
                      />
                    </div>
                  )}
                  {selectedElement.type === 'image' && (
                    <PropertyInput
                      label="URL"
                      value={selectedElement.src}
                      onChange={(val) => handleUpdateElement(selectedElement.id, { src: String(val) })}
                    />
                  )}
                  {selectedElement.type === 'video' && (
                    <>
                      <PropertyInput
                        label="URL"
                        value={selectedElement.src}
                        onChange={(val) => handleUpdateElement(selectedElement.id, { src: String(val) })}
                      />
                      <label className="checkbox-label" style={{ padding: '4px 8px' }}>
                        <input
                          type="checkbox"
                          checked={selectedElement.muted ?? false}
                          onChange={(e) => handleUpdateElement(selectedElement.id, { muted: e.target.checked })}
                        />
                        Muted
                      </label>
                      <label className="checkbox-label" style={{ padding: '4px 8px' }}>
                        <input
                          type="checkbox"
                          checked={selectedElement.loop ?? false}
                          onChange={(e) => handleUpdateElement(selectedElement.id, { loop: e.target.checked })}
                        />
                        Loop
                      </label>
                    </>
                  )}
                  {selectedElement.type === 'button' && (
                    <>
                      <PropertyInput
                        label="Text"
                        value={selectedElement.text}
                        onChange={(val) => handleUpdateElement(selectedElement.id, { text: String(val) })}
                      />
                      <div className="property-row">
                        <label>Font</label>
                        <CustomSelect
                          value={selectedElementEffectiveStyle?.fontFamily || 'Host Grotesk'}
                          onChange={(value) => handleSmartStyleUpdate(selectedElement.id, { fontFamily: value as FontFamily })}
                          options={[
                            { value: 'Host Grotesk', label: 'Host Grotesk' },
                            { value: 'Inter', label: 'Inter' },
                            { value: 'Space Grotesk', label: 'Space Grotesk' },
                            { value: 'Oswald', label: 'Oswald' },
                            { value: 'Michroma', label: 'Michroma' },
                            { value: 'Playfair Display', label: 'Playfair Display' },
                            { value: 'Instrument Serif', label: 'Instrument Serif' },
                            { value: 'IBM Plex Mono', label: 'IBM Plex Mono' },
                            { value: 'Xanh Mono', label: 'Xanh Mono' },
                            { value: 'VT323', label: 'VT323' },
                            { value: 'ChicagoFLF', label: 'Chicago' },
                          ]}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Actions section - for buttons only */}
                {selectedElement.type === 'button' && (
                  <div className="property-section">
                    <div className="section-header-row">
                      <h4>Actions</h4>
                      <button
                        className="icon-btn small"
                        onClick={() => handleAddButtonAction(selectedElement.id)}
                        title="Add Action"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    {(!selectedElement.actions || selectedElement.actions.length === 0) ? (
                      <p className="placeholder small">No actions. Add an action to make this button do something.</p>
                    ) : (
                      <div className="actions-list">
                        {selectedElement.actions.map((action, actionIndex) => (
                          <div key={actionIndex} className="action-item">
                            <div className="action-header">
                              <span className="action-number">Action {actionIndex + 1}</span>
                              <button
                                className="icon-btn small danger"
                                onClick={() => handleRemoveButtonAction(selectedElement.id, actionIndex)}
                                title="Remove Action"
                              >
                                ×
                              </button>
                            </div>
                            <div className="action-row">
                              <label>Type</label>
                              <CustomSelect
                                value={action.type}
                                onChange={(value) => {
                                  const newType = value as 'navigateToLayout' | 'setVariable';
                                  if (newType === 'navigateToLayout') {
                                    handleUpdateButtonAction(selectedElement.id, actionIndex, {
                                      type: 'navigateToLayout',
                                      targetLayout: 'default',
                                    } as ButtonAction);
                                  } else {
                                    handleUpdateButtonAction(selectedElement.id, actionIndex, {
                                      type: 'setVariable',
                                      variableName: '',
                                      value: '',
                                    } as ButtonAction);
                                  }
                                }}
                                options={[
                                  { value: 'setVariable', label: 'Set Variable' },
                                  { value: 'navigateToLayout', label: 'Navigate to Layout' },
                                ]}
                              />
                            </div>
                            {action.type === 'navigateToLayout' && (
                              <div className="action-row">
                                <label>Target</label>
                                <CustomSelect
                                  value={action.targetLayout || ''}
                                  onChange={(value) => handleUpdateButtonAction(selectedElement.id, actionIndex, { targetLayout: value })}
                                  options={layoutIds.map(id => ({ value: id, label: id }))}
                                />
                              </div>
                            )}
                            {action.type === 'setVariable' && (
                              <>
                                <div className="action-row">
                                  <label>Variable</label>
                                  <div className="variable-select-wrapper">
                                    <CustomSelect
                                      value={action.variableName || ''}
                                      onChange={(value) => {
                                        if (value === '__new__') {
                                          const name = prompt('Enter new variable name:');
                                          if (name && name.trim()) {
                                            handleAddVariable(name.trim());
                                            handleUpdateButtonAction(selectedElement.id, actionIndex, { variableName: name.trim() });
                                          }
                                        } else {
                                          handleUpdateButtonAction(selectedElement.id, actionIndex, { variableName: value });
                                        }
                                      }}
                                      options={[
                                        { value: '', label: 'Select variable...' },
                                        ...availableVariables.map(v => ({ value: v.name, label: v.name })),
                                        { value: '__new__', label: '+ Add new variable' },
                                      ]}
                                    />
                                  </div>
                                </div>
                                <div className="action-row">
                                  <label>Value</label>
                                  <input
                                    type="text"
                                    value={action.value || ''}
                                    onChange={(e) => handleUpdateButtonAction(selectedElement.id, actionIndex, { value: e.target.value })}
                                    placeholder="value"
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Position section */}
                <div className="property-section collapsible">
                  <h4
                    onClick={() => setCollapsedSections(prev => {
                      const next = new Set(prev);
                      if (next.has('position')) next.delete('position');
                      else next.add('position');
                      return next;
                    })}
                  >
                    <span className={`collapse-arrow ${collapsedSections.has('position') ? 'collapsed' : ''}`}>▼</span>
                    Position
                  </h4>
                  {!collapsedSections.has('position') && (
                    <div className="property-grid">
                      <PropertyInput
                        label="X"
                        value={selectedElementEffectiveStyle?.x}
                        type="number"
                        onChange={(val) => handleSmartStyleUpdate(selectedElement.id, { x: Number(val) })}
                      />
                      <PropertyInput
                        label="Y"
                        value={selectedElementEffectiveStyle?.y}
                        type="number"
                        onChange={(val) => handleSmartStyleUpdate(selectedElement.id, { y: Number(val) })}
                      />
                    </div>
                  )}
                </div>

                {/* Size section */}
                <div className="property-section collapsible">
                  <h4
                    onClick={() => setCollapsedSections(prev => {
                      const next = new Set(prev);
                      if (next.has('size')) next.delete('size');
                      else next.add('size');
                      return next;
                    })}
                  >
                    <span className={`collapse-arrow ${collapsedSections.has('size') ? 'collapsed' : ''}`}>▼</span>
                    Size
                  </h4>
                  {!collapsedSections.has('size') && (
                    <div className="property-grid">
                      <PropertyInput
                        label="W"
                        value={selectedElementEffectiveStyle?.width}
                        type="number"
                        placeholder={selectedElement.type === 'text' ? 'auto' : ''}
                        onChange={(val) => handleSmartStyleUpdate(selectedElement.id, { width: val as number | undefined })}
                      />
                      <PropertyInput
                        label="H"
                        value={selectedElementEffectiveStyle?.height}
                        type="number"
                        placeholder={selectedElement.type === 'text' ? 'auto' : ''}
                        onChange={(val) => handleSmartStyleUpdate(selectedElement.id, { height: val as number | undefined })}
                      />
                    </div>
                  )}
                </div>

                {/* Appearance section */}
                <div className="property-section">
                  <h4>Appearance</h4>
                  <div className="property-row">
                    <label>Opacity</label>
                    <div className="slider-with-value">
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={selectedElementEffectiveStyle?.opacity ?? 1}
                        onChange={(e) => handleSmartStyleUpdate(selectedElement.id, { opacity: parseFloat(e.target.value) })}
                      />
                      <span className="slider-value">{((selectedElementEffectiveStyle?.opacity ?? 1) * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                  <ColorPicker
                    label="Bg Color"
                    value={selectedElementEffectiveStyle?.backgroundColor}
                    onChange={(val) => handleSmartStyleUpdate(selectedElement.id, { backgroundColor: val })}
                  />
                  {selectedElement.type === 'text' && (
                    <>
                      <ColorPicker
                        label="Color"
                        value={selectedElementEffectiveStyle?.color}
                        onChange={(val) => handleSmartStyleUpdate(selectedElement.id, { color: val })}
                      />
                      <PropertyInput
                        label="Font Size"
                        value={selectedElementEffectiveStyle?.fontSize}
                        type="number"
                        onChange={(val) => handleSmartStyleUpdate(selectedElement.id, { fontSize: Number(val) })}
                      />
                      <div className="property-row">
                        <label>Font</label>
                        <CustomSelect
                          value={selectedElementEffectiveStyle?.fontFamily || 'Host Grotesk'}
                          onChange={(value) => handleSmartStyleUpdate(selectedElement.id, { fontFamily: value as FontFamily })}
                          options={[
                            { value: 'Host Grotesk', label: 'Host Grotesk' },
                            { value: 'Inter', label: 'Inter' },
                            { value: 'Space Grotesk', label: 'Space Grotesk' },
                            { value: 'Oswald', label: 'Oswald' },
                            { value: 'Michroma', label: 'Michroma' },
                            { value: 'Playfair Display', label: 'Playfair Display' },
                            { value: 'Instrument Serif', label: 'Instrument Serif' },
                            { value: 'IBM Plex Mono', label: 'IBM Plex Mono' },
                            { value: 'Xanh Mono', label: 'Xanh Mono' },
                            { value: 'VT323', label: 'VT323' },
                            { value: 'ChicagoFLF', label: 'Chicago' },
                          ]}
                        />
                      </div>
                      <PropertyInput
                        label="Line Height"
                        value={selectedElementEffectiveStyle?.lineHeight}
                        type="number"
                        step="0.1"
                        placeholder="1.2"
                        onChange={(val) => handleSmartStyleUpdate(selectedElement.id, { lineHeight: val as number | undefined })}
                      />
                      <div className="property-row">
                        <label>Align</label>
                        <CustomSelect
                          value={selectedElementEffectiveStyle?.textAlign || 'left'}
                          onChange={(value) => handleSmartStyleUpdate(selectedElement.id, { textAlign: value as 'left' | 'center' | 'right' | 'justify' })}
                          options={[
                            { value: 'left', label: 'Left' },
                            { value: 'center', label: 'Center' },
                            { value: 'right', label: 'Right' },
                            { value: 'justify', label: 'Justify' },
                          ]}
                        />
                      </div>
                      <PropertyInput
                        label="Padding"
                        value={selectedElementEffectiveStyle?.padding}
                        type="number"
                        placeholder="0"
                        onChange={(val) => handleSmartStyleUpdate(selectedElement.id, { padding: val ? Number(val) : undefined })}
                      />
                      <label className="checkbox-label" style={{ padding: '4px 8px' }}>
                        <input
                          type="checkbox"
                          checked={selectedElementEffectiveStyle?.marquee ?? false}
                          onChange={(e) => handleSmartStyleUpdate(selectedElement.id, { marquee: e.target.checked })}
                        />
                        Marquee
                      </label>
                    </>
                  )}
                  <PropertyInput
                    label="Radius"
                    value={selectedElementEffectiveStyle?.borderRadius}
                    type="number"
                    onChange={(val) => handleSmartStyleUpdate(selectedElement.id, { borderRadius: Number(val) })}
                  />
                </div>

                {/* Animations section */}
                <div className="property-section">
                  <div className="section-header-row">
                    <h4>Animations</h4>
                    <button
                      className="icon-btn small"
                      onClick={() => handleAddAnimation(selectedElement.id)}
                      title="Add Animation"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  {(!selectedElement.animations || selectedElement.animations.length === 0) ? (
                    <p className="placeholder small">No animations</p>
                  ) : (
                    <div className="animations-list">
                      {selectedElement.animations.map((anim, animIndex) => (
                        <div key={animIndex} className="animation-item">
                          <div className="animation-header">
                            <CustomSelect
                              value={anim.property}
                              onChange={(value) => handleUpdateAnimation(
                                selectedElement.id,
                                animIndex,
                                { property: value as Animation['property'] }
                              )}
                              options={[
                                { value: 'opacity', label: 'Opacity' },
                                { value: 'scale', label: 'Scale' },
                                { value: 'rotation', label: 'Rotation' },
                                { value: 'translateX', label: 'Translate X' },
                                { value: 'translateY', label: 'Translate Y' },
                              ]}
                            />
                            <button
                              className="icon-btn small danger"
                              onClick={() => handleRemoveAnimation(selectedElement.id, animIndex)}
                              title="Remove Animation"
                            >
                              ×
                            </button>
                          </div>

                          {/* Trigger */}
                          <div className="animation-trigger">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={!!anim.trigger}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    handleUpdateAnimation(selectedElement.id, animIndex, {
                                      trigger: { event: 'playbackPosition', time: 0 }
                                    });
                                  } else {
                                    handleUpdateAnimation(selectedElement.id, animIndex, {
                                      trigger: undefined
                                    });
                                  }
                                }}
                              />
                              Trigger at time
                            </label>
                            {anim.trigger && (
                              <input
                                type="number"
                                className="trigger-time-input"
                                value={anim.trigger.time}
                                onChange={(e) => handleUpdateAnimation(
                                  selectedElement.id,
                                  animIndex,
                                  { trigger: { event: 'playbackPosition', time: parseFloat(e.target.value) || 0 } }
                                )}
                                step="0.1"
                              />
                            )}
                          </div>

                          {/* Loop */}
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={!!anim.loop}
                              onChange={(e) => handleUpdateAnimation(
                                selectedElement.id,
                                animIndex,
                                { loop: e.target.checked }
                              )}
                            />
                            Loop
                          </label>

                          {/* Keyframes */}
                          <div className="keyframes-section">
                            <div className="keyframes-header">
                              <span>Keyframes</span>
                              <button
                                className="icon-btn tiny"
                                onClick={() => handleAddAnimationKeyframe(selectedElement.id, animIndex)}
                                title="Add Keyframe"
                              >
                                <Plus size={12} />
                              </button>
                            </div>
                            <div className="keyframes-list">
                              {anim.keyframes.map((kf, kfIndex) => (
                                <div key={kfIndex} className="keyframe-row">
                                  <input
                                    type="number"
                                    value={kf.at}
                                    onChange={(e) => handleUpdateKeyframe(
                                      selectedElement.id,
                                      animIndex,
                                      kfIndex,
                                      { at: parseFloat(e.target.value) || 0 }
                                    )}
                                    step="0.1"
                                    placeholder="at"
                                    title="Time (seconds)"
                                  />
                                  <input
                                    type="number"
                                    value={kf.value}
                                    onChange={(e) => handleUpdateKeyframe(
                                      selectedElement.id,
                                      animIndex,
                                      kfIndex,
                                      { value: parseFloat(e.target.value) || 0 }
                                    )}
                                    step="0.1"
                                    placeholder="value"
                                    title="Value"
                                  />
                                  <CustomSelect
                                    value={kf.easing || 'linear'}
                                    onChange={(value) => handleUpdateKeyframe(
                                      selectedElement.id,
                                      animIndex,
                                      kfIndex,
                                      { easing: value as EasingFunction }
                                    )}
                                    options={[
                                      { value: 'linear', label: 'Linear' },
                                      { value: 'easeIn', label: 'Ease In' },
                                      { value: 'easeOut', label: 'Ease Out' },
                                      { value: 'easeInOut', label: 'Ease In-Out' },
                                    ]}
                                  />
                                  {anim.keyframes.length > 2 && (
                                    <button
                                      className="icon-btn tiny danger"
                                      onClick={() => handleRemoveKeyframe(selectedElement.id, animIndex, kfIndex)}
                                      title="Remove Keyframe"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Delete buttons */}
                <div className="property-section delete-section">
                  {selectedStateId && (
                    <>
                      <button
                        className="delete-btn secondary"
                        onClick={() => {
                          if (window.confirm('Delete this state? This cannot be undone.')) {
                            handleRemoveElementState(selectedElement.id, selectedStateId);
                          }
                        }}
                      >
                        Delete State
                      </button>
                      <div className="delete-divider" />
                    </>
                  )}
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteElement(selectedElement.id)}
                  >
                    Delete Element
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <TimelinePanel
          showKeyframePanel={showKeyframePanel}
          setShowKeyframePanel={setShowKeyframePanel}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          selectedKeyframeTime={selectedKeyframeTime}
          setSelectedKeyframeTime={setSelectedKeyframeTime}
          selectedElementId={selectedElementId}
          setSelectedElementId={setSelectedElementId}
          handleAddKeyframe={handleAddKeyframe}
          handleDeleteKeyframe={handleDeleteKeyframe}
          playbackTime={playbackTime}
          setPlaybackTime={setPlaybackTime}
          timelineDuration={timelineDuration}
          elementsWithKeyframes={elementsWithKeyframes}
          previewTrackId={previewTrackId}
          setPreviewTrackId={setPreviewTrackId}
          tracks={album?.tracks}
        />
      </div>
      )}
      {showAiDocsModal && (
        <div
          className="ai-docs-modal-backdrop"
          onClick={() => setShowAiDocsModal(false)}
        >
          <div
            className="ai-docs-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="AI reference docs"
          >
            <div className="ai-docs-modal-header">
              <h2>AI reference docs</h2>
              <button
                className="ai-docs-modal-copy"
                onClick={async () => {
                  await navigator.clipboard.writeText(AI_DOCS);
                  setCopiedAiDocs(true);
                  setTimeout(() => setCopiedAiDocs(false), 1500);
                }}
              >
                {copiedAiDocs ? 'Copied!' : 'Copy'}
              </button>
              <button
                className="ai-docs-modal-close"
                onClick={() => setShowAiDocsModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <pre className="ai-docs-modal-body">{AI_DOCS}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
